import { useState, type MouseEvent } from 'react'
import { IconButton, Menu, MenuItem, CircularProgress, Alert, Stack } from '@mui/material'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { useQueryClient } from '@tanstack/react-query'
import { exportAsImage } from '~/util/export/exportImage'
import { exportAsPdf } from '~/util/export/exportPdf'
import { pinToSheet } from '~/util/export/chartData/pinToSheet'
import { buildWorkbook } from '~/util/export/chartData/buildWorkbook'
import { useApiClient } from '~/api/useApiClient'
import type { PinV1 } from '~/pin/PinV1'
import { DIALOGS } from '~/components/Charts/chrome/chartChrome'

interface ExportMenuProps {
  pin: PinV1
  target: () => HTMLElement | null
  filenameBase: string
}

type Format = 'png' | 'pdf' | 'xlsx'

export function ExportMenu({ pin, target, filenameBase }: ExportMenuProps) {
  const client = useApiClient()
  const queryClient = useQueryClient()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleOpen = (e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)
  const handleClose = () => setAnchor(null)

  const run = async (format: Format) => {
    handleClose()
    setError(null)
    if (format === 'xlsx') {
      setPending(true)
      try {
        await buildWorkbook(
          [await pinToSheet(pin, { client, queryClient })],
          `${filenameBase}.xlsx`,
        )
      } catch (err) {
        console.error('[ExportMenu] data export failed', err)
        setError(DIALOGS.exportFailed)
      } finally {
        setPending(false)
      }
      return
    }

    const el = target()
    if (!el) return

    setPending(true)
    try {
      if (format === 'png') {
        await exportAsImage(el, `${filenameBase}.png`)
      } else {
        await exportAsPdf(el, `${filenameBase}.pdf`)
      }
    } catch (err) {
      console.error(`[ExportMenu] ${format} export failed`, err)
      setError(DIALOGS.exportFailed)
    } finally {
      setPending(false)
    }
  }

  return (
    <Stack spacing={0.5} alignItems="flex-end">
      <IconButton
        size="small"
        aria-label={DIALOGS.exportAria}
        onClick={handleOpen}
        disabled={pending}
      >
        {pending ? <CircularProgress size={16} /> : <MoreHorizIcon />}
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={handleClose}>
        <MenuItem onClick={() => run('xlsx')}>{DIALOGS.exportData}</MenuItem>
        <MenuItem onClick={() => run('png')}>{DIALOGS.exportAsImage}</MenuItem>
        <MenuItem onClick={() => run('pdf')}>{DIALOGS.exportAsPdf}</MenuItem>
      </Menu>
      {error ? <Alert severity="error">{error}</Alert> : null}
    </Stack>
  )
}
